import type { ReactNode } from "react";
import { Link, useLocation } from "wouter";

import { Button } from "@/components/ui/button.tsx";
import { type UserTaskAnswer, userTaskHref } from "@/types/userTaskLinks.ts";
import { taskErrorMessage } from "@/shared/userTaskErrors.ts";
import { answerRequest, useAnswerUserTask } from "./api.ts";

/**
 * A brief's one-tap answer (`/tasks/:id?answer=done`), as a button in the
 * thread. It answers on the tap, never on render, and says what happened in
 * place; Hand to Okthx opens the conversation okthx starts for it. When the
 * answer does not go through — the task moved on, or the list is off — it says
 * why, with a link to the task.
 */
export const UserTaskAnswerButton = ({
  taskId,
  answer,
  children,
}: {
  taskId: string;
  answer: UserTaskAnswer;
  children: ReactNode;
}) => {
  const mutation = useAnswerUserTask();
  const [, navigate] = useLocation();

  if (mutation.isSuccess) {
    return <span className="text-xs text-muted-foreground">✓ {children}</span>;
  }
  if (mutation.isError) {
    return (
      <span className="text-xs">
        {taskErrorMessage(mutation.error, "Couldn't answer.", {
          // Only the status answers send `fromStatus`; Keep, Dismiss, Got it,
          // Still waiting and Hand to Okthx carry no precondition, so their
          // refusals are the server's own and it says them better.
          stale: answerRequest(answer).route === "status",
        })}{" "}
        <Link href={userTaskHref(taskId)} className="link">
          Open the task
        </Link>
      </span>
    );
  }
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="mx-0.5 h-6 px-2 align-baseline text-xs"
      data-statsig-name={`tasks-brief-${answer}`}
      disabled={mutation.isPending}
      onClick={() =>
        mutation.mutate(
          { taskId, answer },
          {
            onSuccess: ({ threadId }) => {
              if (answer === "handOff" && threadId) navigate(`/threads/${threadId}`);
            },
          },
        )
      }
    >
      {children}
    </Button>
  );
};
